/**
 * RG Video Carousel JavaScript
 * Handles play/pause toggle, mute/unmute, and scroll navigation with progress
 */

class RGVideoCarousel {
    constructor(container) {
        this.container = container;
        this.track = container.querySelector('[data-rg-track]');
        this.cards = container.querySelectorAll('[data-rg-video-card]');
        this.prevBtn = container.querySelector('[data-rg-nav="prev"]');
        this.nextBtn = container.querySelector('[data-rg-nav="next"]');
        this.progressFill = container.querySelector('[data-rg-progress-fill]');

        this.init();
    }

    init() {
        if (!this.track) return;

        // Scroll Event for Progress & Arrows
        this.track.addEventListener('scroll', () => {
            this.updateProgress();
        });

        // Resize support
        window.addEventListener('resize', () => {
            this.checkOverflow();
            this.updateProgress();
        });

        // Initial check
        // Check after a small delay to ensure rendering is complete
        setTimeout(() => {
            this.checkOverflow();
            this.updateProgress();
        }, 100);

        // Card Interactions
        this.cards.forEach(card => {
            const video = card.querySelector('[data-rg-video]');
            const muteBtn = card.querySelector('[data-rg-mute-btn]');
            const muteIcon = card.querySelector('[data-rg-mute-icon]');

            if (video) {
                // Ensure muted by default
                video.muted = true;

                // Play/Pause on Card Click
                card.addEventListener('click', (e) => {
                    if (e.target.closest('[data-rg-mute-btn]')) return;
                    this.togglePlay(video, card);
                });

                // Mute/Unmute
                muteBtn?.addEventListener('click', (e) => {
                    e.stopPropagation();
                    this.toggleMute(video, muteIcon);
                });

                // When video ends
                video.addEventListener('ended', () => {
                    card.classList.remove('rg-playing');
                });
            }
        });

        // Navigation (Arrows)
        this.prevBtn?.addEventListener('click', (e) => {
            e.preventDefault();
            this.scroll('left');
        });
        this.nextBtn?.addEventListener('click', (e) => {
            e.preventDefault();
            this.scroll('right');
        });
    }

    // Show/Hide arrows based on content overflow
    checkOverflow() {
        if (!this.track) return;

        // Check if content overflows container
        const isOverflowing = this.track.scrollWidth > this.track.clientWidth;

        if (isOverflowing) {
            this.prevBtn?.classList.add('rg-visible');
            this.nextBtn?.classList.add('rg-visible');
        } else {
            this.prevBtn?.classList.remove('rg-visible');
            this.nextBtn?.classList.remove('rg-visible');
        }
    }

    updateProgress() {
        if (!this.track || !this.progressFill) return;

        const scrollLeft = this.track.scrollLeft;
        const scrollWidth = this.track.scrollWidth;
        const clientWidth = this.track.clientWidth;

        // Calculate percentage
        // if no scroll possible, 100%
        let progress = 0;
        if (scrollWidth > clientWidth) {
            progress = (scrollLeft / (scrollWidth - clientWidth)) * 100;
        } else {
            progress = 100;
        }

        this.progressFill.style.width = `${progress}%`;
    }

    async togglePlay(video, card) {
        try {
            if (video.paused) {
                this.pauseAllOthers(video);
                await video.play();
                card.classList.add('rg-playing');
            } else {
                video.pause();
                card.classList.remove('rg-playing');
            }
        } catch (err) {
            console.error("Video play failed:", err);
            card.classList.remove('rg-playing');
        }
    }

    toggleMute(video, icon) {
        video.muted = !video.muted;
        if (icon) {
            icon.textContent = video.muted ? 'volume_off' : 'volume_up';
        }
    }

    pauseAllOthers(currentVideo) {
        this.cards.forEach(card => {
            const v = card.querySelector('[data-rg-video]');
            if (v && v !== currentVideo && !v.paused) {
                v.pause();
                card.classList.remove('rg-playing');
            }
        });
    }

    scroll(direction) {
        if (!this.track) return;

        const cardWidth = 320; // Default width

        // Try to get dynamic width from first card
        let scrollAmount = cardWidth;
        const card = this.track.querySelector('.rg-video-carousel__card');
        if (card) {
            const style = window.getComputedStyle(this.track);
            const gap = parseFloat(style.columnGap) || 16;
            scrollAmount = card.offsetWidth + gap;
        }

        const targetScroll = direction === 'left'
            ? this.track.scrollLeft - scrollAmount
            : this.track.scrollLeft + scrollAmount;

        this.track.scrollTo({
            left: targetScroll,
            behavior: 'smooth'
        });
    }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
    const carousels = document.querySelectorAll('[data-rg-video-carousel]');
    carousels.forEach(carousel => new RGVideoCarousel(carousel));
});

// Shopify Theme Editor Support
if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
        const carousel = event.target.querySelector('[data-rg-video-carousel]');
        if (carousel) {
            new RGVideoCarousel(carousel);
        }
    });
}
